// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { ShieldCheck, Server, Lock, Eye, ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Checkbox } from '@/components/ui'
import { ROUTES } from '@/lib/constants/routes'
import type { OnboardingData } from '@/types'

const PRIVACY_POINTS = [
  {
    icon:  <Lock     className="h-4 w-4 text-sage-600" />,
    text:  'Uw gegevens worden versleuteld opgeslagen op uw apparaat.',
  },
  {
    icon:  <Server   className="h-4 w-4 text-sage-600" />,
    text:  'In deze versie worden uw documenten, wachtwoorden en financiële gegevens lokaal op uw apparaat opgeslagen.',
  },
  {
    icon:  <Eye      className="h-4 w-4 text-sage-600" />,
    text:  'De inhoud van uw gegevens wordt versleuteld opgeslagen en is niet inzichtelijk voor WilsApp.',
  },
  {
    icon:  <ShieldCheck className="h-4 w-4 text-sage-600" />,
    text:  'U kunt uw account en opgeslagen gegevens op elk moment verwijderen via Instellingen.',
  },
]

interface Step9PrivacyProps {
  data:     Partial<OnboardingData>
  onChange: (updates: Partial<OnboardingData>) => void
  errors:   Partial<Record<keyof OnboardingData, string>>
}

export function Step9Privacy({ data, onChange, errors }: Step9PrivacyProps) {
  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-lg font-semibold text-ink mb-1">Privacy & Voorwaarden</h2>
        <p className="text-sm text-ink-3">
          Lees hoe wij omgaan met uw gegevens voordat u begint.
        </p>
      </div>

      {/* Privacy commitments */}
      <div className="rounded-xl bg-sage-50 border border-sage-100 p-4 flex flex-col gap-3">
        <p className="text-xs font-semibold text-sage-800 uppercase tracking-wider">
          Uw privacy in het kort
        </p>
        {PRIVACY_POINTS.map((point, i) => (
          <div key={i} className="flex items-start gap-2.5">
            <div className="flex-shrink-0 mt-0.5">{point.icon}</div>
            <p className="text-xs text-sage-700 leading-relaxed">{point.text}</p>
          </div>
        ))}
      </div>

      {/* Checkboxes with links to actual documents */}
      <div className="flex flex-col gap-3.5">
        <div className="flex flex-col gap-1">
          <Checkbox
            label="Ik ga akkoord met de privacyverklaring van WilsApp"
            description="WilsApp verwerkt uw gegevens conform de AVG."
            checked={data.privacyAccepted ?? false}
            onChange={(e) => onChange({ privacyAccepted: e.target.checked })}
            error={errors.privacyAccepted}
            required
          />
          <Link to={ROUTES.PRIVACY} target="_blank" rel="noopener noreferrer"
            className="ml-7 text-xs text-sage-600 underline underline-offset-2 flex items-center gap-1">
            <ExternalLink className="h-3 w-3" />
            Lees de volledige privacyverklaring
          </Link>
        </div>

        <div className="flex flex-col gap-1">
          <Checkbox
            label="Ik ga akkoord met de algemene voorwaarden"
            description="WilsApp is een persoonlijk vastlegdossier en ondersteunend hulpmiddel. WilsApp is geen officiële bron, geen juridisch document en vervangt geen juridisch, notarieel of medisch advies."
            checked={data.termsAccepted ?? false}
            onChange={(e) => onChange({ termsAccepted: e.target.checked })}
            error={errors.termsAccepted}
            required
          />
          <Link to={ROUTES.TERMS} target="_blank" rel="noopener noreferrer"
            className="ml-7 text-xs text-sage-600 underline underline-offset-2 flex items-center gap-1">
            <ExternalLink className="h-3 w-3" />
            Lees de algemene voorwaarden
          </Link>
        </div>
      </div>

      {/* P1.3: corrected text - no longer promises inzage/exporteren */}
      <p className="text-xs text-ink-4 leading-relaxed">
        U kunt uw account en alle bijbehorende gegevens op elk moment volledig verwijderen
        via Instellingen. De privacyverklaring en algemene voorwaarden zijn ook terug te
        vinden via het instellingenmenu.
      </p>
    </div>
  )
}
